/**
 * @ngdoc service
 * @name app.livefeedService
 * @description
 * # livefeedService
 * Service to talk with backend api.
 * @requires app.apiService
 * @requires app.localService
 * @requires constants:config
 * @requires constants:storageKeys
 **/

/*---------------------------------------------------------*\
 * Imports
\*---------------------------------------------------------*/
import angular from 'angular';

/*---------------------------------------------------------*\
 * livefeedService
\*---------------------------------------------------------*/
/* @ngInject */
class livefeedService {
  //-----------------------------------
  // Constructor
  //-----------------------------------
  constructor(config, localService, storageKeys) {
    this.config = config;
    this.localService = localService;
    this.storageKeys = storageKeys;
    /**
     * @ngdoc property
     * @name app.livefeedService#allRegions
     * @propertyOf app.livefeedService
     * @example
     * ``` js
     * livefeedService.allRegions;
     * ```
     * @description
     * It calls the localService with all available as param.
     * @returns {array} Array containing all available regions.
     **/
    this.allRegions = this.localService.get(this.storageKeys.regions);
    this.getCurrentRegion();
  }
  //-----------------------------------
  // Properties
  //-----------------------------------
  /**
   * @ngdoc
   * @name app.livefeedService#getCurrentRegion
   * @methodOf app.livefeedService
   * @example
   * ``` js
   * livefeedService.getCurrentRegion();
   * ```
   * @description
   * Service to talk with backend api.
   * It calls the localService with the selected regions as a param.
   * @returns {object} it returns the region object. This regions object can be the either the one returned from
   * {@link app.localService localService} or the default one which is:
   * ``` js
   * {
   *  abbr: 'LI',
   *  name: 'Long Island',
   *  callSign: 'N12LI_WEST',
   *  twCallSign: 'N12TW_LI'
   * }
   * ```
   **/
  getCurrentRegion() {
    const region = this.localService.get(this.storageKeys.selectedRegion);
    return region || { abbr: 'LI', name: 'Long Island', callSign: 'N12LI_WEST', twCallSign: 'N12TW_LI' };
  }

  //-----------------------------------
  // Methods
  //-----------------------------------
  /**
   * @ngdoc
   * @name app.livefeedService#getLivefeed
   * @methodOf app.livefeedService
   * @example
   * ``` js
   * livefeedService.getLivefeed();
   * ```
   * @description
   * calls {@link app.livefeedService#getCurrentRegion getCurrentRegion} method.
   * calls {@link app.livefeedService#cachebust cachebust} method with params absolute url and current region callsign.
   * @returns {httpPromise} Promise that will resolve into a string url for livefeed video.
   **/
  getLivefeed() {
    const region = this.getCurrentRegion();
    return this.cachebust(this.config.absBaseUrlLiveFeed.replace('{callSign}', region.callSign));
  }
  /**
   * @ngdoc
   * @name app.livefeedService#getTWLivefeed
   * @methodOf app.livefeedService
   * @example
   * ``` js
   * livefeedService.getTWLivefeed(callSign);
   * ```
   * @param {string} callSign string passed showing current region callSign.
   * @description
   * calls {@link app.livefeedService#cachebust cachebust} method with params absolute url and current region callsign.
   * @returns {httpPromise} Promise that will resolve into a string url for livefeed video for traffic and weather.
   **/
  getTWLivefeed(twCallSign) {
    return this.cachebust(this.config.absBaseUrlLiveFeed.replace('{callSign}', twCallSign));
  }
  /**
   * @ngdoc
   * @name app.livefeedService#cachebust
   * @methodOf app.livefeedService
   * @example
   * ``` js
   * livefeedService.cachebust('http://www.randomurl.com/');
   * ```
   * @param {string} url string passed in.
   * @description
   * transforms the string url by adding a timestamp as a parameter
   * @returns {string} converted string with added timestamp.
   **/
  cachebust(url) {
    return url + '&' + Math.round(new Date().getTime() / 1000);
  }
}

livefeedService.$inject = [
  'config',
  'localService',
  'storageKeys'
];

const api = angular
  .module('livefeedService', ['config', 'constants', 'regionService', 'localService'])
  .service('livefeedService', livefeedService);

export default api;



/** WEBPACK FOOTER **
 ** ./client/app/services/apis/livefeed.js
 **/